import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Input from "@mui/joy/Input";
import EmptyData from "../../components/EmptyData";
import CustomPagination from "../../components/CustomPagination";
import TranslateText from "../../components/TranslateText";
import { get_all_staff, get_contact_lists } from "../../reduxData/user/userAction";
import "../../css/contact.css";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../hooks/formatDateTime";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import Select from "react-select";

const Contacts = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { accessToken, user } = useSelector((state) => state.auth);
    const { contactLists, totalContacts, contactStats, staffList } = useSelector((state) => state.user);
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [priority, setPriority] = useState("");
    const [status, setStatus] = useState("");
    const [assignedStaff, setAssignedStaff] = useState("");
    const [selectedStaffOption, setSelectedStaffOption] = useState(null);
    const [staffSearch, setStaffSearch] = useState("");
    const [isStaffLoading, setIsStaffLoading] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const CATEGORY_MAP = useMemo(() => ({
        GENERAL: t("queries.categories.generalInquiry"),
        ORDER: t("queries.categories.orderIssue"),
        PAYMENT: t("queries.categories.paymentProblem"),
        SHIPPING: t("queries.categories.shippingDelivery"),
        RETURN: t("queries.categories.refundDispute"),
        ACCOUNT: t("queries.categories.accountSupport"),
        TECHNICAL: t("queries.categories.technicalSupport"),
        OTHER: t("queries.categories.other"),
    }), [t]);

    const PRIORITY_OPTIONS = useMemo(() => ([
        { value: "URGENT", label: t("queries.priorityValues.urgent") },
        { value: "HIGH", label: t("queries.priorityValues.high") },
        { value: "MEDIUM", label: t("queries.priorityValues.medium") },
        { value: "LOW", label: t("queries.priorityValues.low") },
    ]), [t]);

    const STATUS_OPTIONS = useMemo(() => ([
        { value: "PENDING", label: t("queries.statusValues.pending") },
        { value: "IN_PROGRESS", label: t("queries.statusValues.in_progress") },
        { value: "RESOLVED", label: t("queries.statusValues.resolved") },
        { value: "CLOSED", label: t("queries.statusValues.closed") },
    ]), [t]);

    useEffect(() => {
        if (!accessToken) return;
        const timer = setTimeout(async () => {
            setIsStaffLoading(true);
            await get_all_staff(dispatch, 1, 6, staffSearch.trim(), user?.id);
            setIsStaffLoading(false);
        }, staffSearch ? 350 : 0);

        return () => clearTimeout(timer);
    }, [accessToken, dispatch, staffSearch, user?.id]);

    useEffect(() => {
        if (!accessToken) return;
        get_contact_lists(dispatch, currentPage, perPage, name, {
            category,
            priority,
            status,
            assign_to: assignedStaff,
            start_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "",
            end_date: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
        });
    }, [accessToken, assignedStaff, category, currentPage, dispatch, fromDate, name, perPage, priority, status, toDate]);

    const normalizeValue = (value, fallback = "") => String(value || fallback).toUpperCase();

    const formatValue = (value, fallback = "-") => {
        if (!value) return fallback;
        return String(value).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const handleDateChange = (setter) => (value) => {
        setter(value);
        setCurrentPage(1);
    };

    const datePickerSlotProps = {
        textField: {
            fullWidth: true,
            size: "small",
        },
    };

    const staffOptions = useMemo(() => staffList?.map((staff) => ({
        value: staff?.id || staff?._id,
        label: staff?.user_name || staff?.name || staff?.email,
        email: staff?.email || "",
    })).filter((option) => option.value && option.label) || [], [staffList]);

    const clearFilters = () => {
        setName("");
        setCategory("");
        setPriority("");
        setStatus("");
        setAssignedStaff("");
        setSelectedStaffOption(null);
        setStaffSearch("");
        setFromDate(null);
        setToDate(null);
        setCurrentPage(1);
    };

    const getStatValue = (keys, fallbackStatus) => {
        const source = contactStats || {};
        const directValue = keys.map((key) => source?.[key]).find((value) => value !== undefined && value !== null);
        if (directValue !== undefined) return Number(directValue || 0);
        return contactLists?.filter((item) => normalizeValue(item?.status, "PENDING") === fallbackStatus).length || 0;
    };

    const statCards = [
        {
            className: "total",
            icon: "fa-regular fa-rectangle-list",
            value: contactStats?.total || contactStats?.totalContacts || totalContacts,
            label: t("queries.totalTickets"),
        },
        {
            className: "pending",
            icon: "fa-regular fa-clock",
            value: getStatValue(["PENDING", "pending"], "PENDING"),
            label: t("queries.statusValues.pending"),
        },
        {
            className: "progress",
            icon: "fa-solid fa-spinner",
            value: getStatValue(["IN_PROGRESS", "in_progress", "inProgress"], "IN_PROGRESS"),
            label: t("queries.inProgress"),
        },
        {
            className: "resolved",
            icon: "fa-regular fa-circle-check",
            value: getStatValue(["RESOLVED", "resolved"], "RESOLVED"),
            label: t("queries.resolved"),
        },
    ];

    const StatusBadge = ({ status }) => {
        const statusClassMap = {
            PENDING: "pending",
            IN_PROGRESS: "progress",
            RESOLVED: "resolved",
            CLOSED: "closed",
        };
        const currentStatus = normalizeValue(status, "PENDING");
        const label = STATUS_OPTIONS.find((option) => option.value === currentStatus)?.label || formatValue(currentStatus);

        return (
            <span className={`ticket-badge status ${statusClassMap[currentStatus] || "pending"}`}>
                {label}
            </span>
        );
    };

    const PriorityBadge = ({ priority }) => {
        const currentPriority = normalizeValue(priority, "LOW");
        const label = PRIORITY_OPTIONS.find((option) => option.value === currentPriority)?.label || formatValue(currentPriority);

        return (
            <span className={`ticket-badge priority ${currentPriority.toLowerCase()}`}>
                {label}
            </span>
        );
    };

    const handleDetail = (item) => {
        navigate(`/query/${item?.id}`, {
            state: {
                data: {
                    ...item,
                    priority: item?.priority || "LOW",
                },
            },
        });
    };

    return (
        <div className="ticket-page">
            <h2 className="support-tile">{t("queries.supportTickets")}</h2>
            <p>{t("queries.supportSubtitle")}</p>
            {/* <div className="ticket-page-header">
                <div>
                    <span className="ticket-page-kicker">{t("queries.supportDesk")}</span>
                </div>
            </div> */}

            <div className="ticket-stats">
                {statCards.map((stat) => (
                    <div className={`stat-card stat-card-${stat.className} `} key={stat.className}>
                        <div className="stat-icon">
                            <i className={stat.icon}></i>
                        </div>
                        <div className="ticket-filter-date">
                            <h2>{stat.value || 0}</h2>
                            <span>{stat.label}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="ticket-filters audit-filter-card">
                <div className="ticket-filter-grid">
                    <div className="ticket-filter-search">
                        <label className="audit-label">{t("queries.user")}</label>
                        <Input placeholder={t("queries.searchPlaceholder")} value={name} style={{ borderRadius: '12px' }} onChange={handleFilterChange(setName)} />
                    </div>
                    <div>
                        <label className="audit-label">{t("queries.category")}</label>
                        <select className="form-select audit-select" value={category} onChange={handleFilterChange(setCategory)}>
                            <option value="">{t("queries.allCategories")}</option>
                            {Object.entries(CATEGORY_MAP).map(([key, value]) => <option key={key} value={key}>{value}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="audit-label">{t("queries.priority")}</label>
                        <select className="form-select audit-select" value={priority} onChange={handleFilterChange(setPriority)}>
                            <option value="">{t("queries.allPriority")}</option>
                            {PRIORITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="audit-label">{t("queries.status")}</label>
                        <select className="form-select audit-select" value={status} onChange={handleFilterChange(setStatus)}>
                            <option value="">{t("queries.allStatus")}</option>
                            {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    <div className="ticket-filter-staff">
                        <label className="audit-label">{t("queries.assignedStaff")}</label>
                        <Select
                            classNamePrefix="ticket-staff-select"
                            isClearable
                            isSearchable
                            isLoading={isStaffLoading}
                            options={staffOptions}
                            placeholder={t("queries.searchStaff")}
                            value={selectedStaffOption}
                            inputValue={staffSearch}
                            onInputChange={(value, meta) => {
                                if (meta.action === "input-change") setStaffSearch(value);
                            }}
                            onChange={(option) => {
                                setSelectedStaffOption(option || null);
                                setAssignedStaff(option?.value || "");
                                setStaffSearch("");
                                setCurrentPage(1);
                            }}
                            filterOption={() => true}
                            formatOptionLabel={(option, { context }) => context === "menu" ? (
                                <div className="ticket-staff-option">
                                    <span>{option.label}</span>
                                    {option.email && <small>{option.email}</small>}
                                </div>
                            ) : option.label}
                            maxMenuHeight={286}
                            noOptionsMessage={() => t("queries.noStaffFound")}
                        />
                    </div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="ticket-filter-date">
                            <label className="audit-label">{t("queries.fromDate")}</label>
                            <DatePicker value={fromDate} maxDate={toDate || dayjs()} onChange={handleDateChange(setFromDate)} slotProps={datePickerSlotProps} />
                        </div>
                        <div>
                            <label className="audit-label">{t("queries.toDate")}</label>
                            <DatePicker value={toDate} minDate={fromDate || undefined} maxDate={dayjs()} onChange={handleDateChange(setToDate)} slotProps={datePickerSlotProps} />
                        </div>
                    </LocalizationProvider>
                    <button className="btn audit-clear-btn" onClick={clearFilters}>{t("queries.clearFilters")}</button>
                </div>
                <div className="audit-filter-footer">

                </div>
            </div>

            <div className="ticket-table-wrapper">
                <table className="table ticket-table align-middle">
                    <thead>
                        <tr>
                            <th>{t("queries.ticketId")}</th>
                            <th>{t("queries.user")}</th>
                            <th>{t("queries.category")}</th>
                            <th>{t("queries.priority")}</th>
                            <th>{t("queries.status")}</th>
                            <th>{t("queries.assignedStaff")}</th>
                            <th>{t("queries.created")}</th>
                            <th>{t("queries.action")}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {contactLists?.length > 0 ? (
                            contactLists.map((item, index) => (
                                <tr key={item?._id || item?.id || index}>
                                    <td>
                                        <span className="ticket-id">
                                            {item?.ticket_id || `TK-${String(item?.id || index + 1).padStart(4, "0")}`}
                                        </span>
                                    </td>

                                    <td>
                                        <div className="d-flex align-items-center gap-3">
                                            {/* <div className="user-avatar">
                                                {item?.name?.charAt(0)?.toUpperCase() || "U"}
                                            </div> */}

                                            <div>
                                                <div className="fw-semibold">
                                                    {item?.name || "-"}
                                                </div>

                                                <small className="text-muted">
                                                    {item?.email || "-"}
                                                </small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {CATEGORY_MAP[normalizeValue(item?.category)] || formatValue(item?.category)}
                                    </td>

                                    <td>
                                        <PriorityBadge priority={item?.priority} />
                                    </td>

                                    <td>
                                        <StatusBadge status={item?.status} />
                                    </td>

                                    <td>
                                        {item?.assigned_to_name || item?.assigned_staff || t("queries.unassigned")}
                                    </td>

                                    <td>
                                        {formatDateTime(item?.created_at)}
                                    </td>

                                    <td>
                                        <button className="si-view-btn" onClick={() => handleDetail(item)}>
                                            {t("order.View")}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <EmptyData />
                        )}
                    </tbody>
                </table>
            </div>

            {totalContacts > 0 && (
                <CustomPagination
                    total={totalContacts}
                    onPageChange={(page, selectedPerPage) => {
                        setCurrentPage(page);
                        setPerPage(selectedPerPage);
                    }}
                />
            )}
        </div>
    );
};

export default Contacts;
